import { useState } from 'react'
import { api } from '../lib/api'
import { formatRelativeActivity } from '../lib/format'
import AddStaffForm from './AddStaffForm'
import ConfirmDialog from './ConfirmDialog'
import ThumbIcon from './ThumbIcon'
import { variablePrefill } from '../lib/variables'

// The space's roster, for both roles. The principal's people, what each
// one's conversation is about (one column per template variable), how far
// each has got, and how long every call ran. Actions are Send, Revoke and
// Reissue, each behind a confirm, plus "Send all unsent" with one summary
// message.
//
// `admin` adds what only an admin's rows carry: the name opens the write-up,
// the New chip and the verdict show beside the status, a Copy link action
// reads the participant's URL from the admin detail route, and rows filed
// away earlier stay hidden. A principal's rows carry none of that, so for her
// nothing here can show a transcript or a write-up.
//
// Stacked cards on a phone, a grid from 768px; the grid's columns come from a
// CSS variable so the variable count can change per template.

const CHIP_CLASS = {
  not_sent: 'chip chip--muted',
  sent: 'chip chip--info',
  opened: 'chip chip--info',
  completed: 'chip chip--success',
  expired: 'chip chip--warning',
  revoked: 'chip chip--muted',
}

const REVOCABLE = ['sent', 'opened']
const REISSUABLE = ['revoked', 'expired']
const LIVE = ['not_sent', 'sent', 'opened', 'completed']

function callLength(secs) {
  if (!Number.isFinite(secs)) return '—'
  const minutes = Math.floor(secs / 60)
  const seconds = String(Math.floor(secs % 60)).padStart(2, '0')
  return `${minutes}:${seconds}`
}

export default function Roster({ space, onChanged, onFlash, admin = false, onOpenResult = null }) {
  const [adding, setAdding] = useState(false)
  const [confirm, setConfirm] = useState(null) // { action, row }
  const [busy, setBusy] = useState(null) // a row key, or 'all'

  const rows = (space.staff ?? []).filter((row) => !admin || !row.archived_at)
  const variables = space.template_variables ?? []
  const links = `/api/v1/spaces/${space.id}/links`
  const unsent = rows.filter((row) => row.link_id && row.status === 'not_sent')
  const name = (row) => `${row.first_name} ${row.last_name}`
  const key = (row) => row.link_id ?? row.participant_id

  async function send(row) {
    await api(`${links}/${row.link_id}/send`, { method: 'POST' })
  }

  async function act(action, row) {
    setBusy(key(row))
    setConfirm(null)
    try {
      if (action === 'send') {
        await send(row)
        onFlash({ type: 'success', text: `Invitation sent to ${name(row)}.` })
      } else if (action === 'revoke') {
        await api(`${links}/${row.link_id}/revoke`, { method: 'POST' })
        onFlash({ type: 'success', text: `${name(row)}'s link no longer works.` })
      } else if (action === 'reissue') {
        await api(`${links}/${row.link_id}/reissue`, { method: 'POST' })
        onFlash({
          type: 'success',
          text: `A fresh link is drafted for ${name(row)} — send it when you're ready.`,
        })
      }
      onChanged()
    } catch (err) {
      onFlash({ type: 'error', text: err.detail || `Could not ${action} ${name(row)}'s link.` })
    } finally {
      setBusy(null)
    }
  }

  // Admin only: the participant's URL comes from the admin detail route — the
  // one payload that carries the token — and goes to the clipboard, or into
  // the flash when the clipboard is not available.
  async function copyLink(row) {
    setBusy(key(row))
    try {
      const detail = await api(`${links}/${row.link_id}`)
      try {
        await navigator.clipboard.writeText(detail.link)
        onFlash({ type: 'success', text: `${name(row)}'s link copied.` })
      } catch {
        onFlash({ type: 'success', text: `${name(row)}'s link: ${detail.link}` })
      }
    } catch (err) {
      onFlash({ type: 'error', text: err.detail || 'Could not read the link.' })
    } finally {
      setBusy(null)
    }
  }

  // One by one, then one message: who went out, and who did not.
  async function sendAll() {
    setBusy('all')
    setConfirm(null)
    const failed = []
    for (const row of unsent) {
      try {
        await send(row)
      } catch {
        failed.push(name(row))
      }
    }
    const sent = unsent.length - failed.length
    const summary = `Sent ${sent} invitation${sent === 1 ? '' : 's'}.`
    onFlash(
      failed.length === 0
        ? { type: 'success', text: summary }
        : {
            type: 'error',
            text: `${summary} Could not send to ${failed.join(', ')} — try again in a moment.`,
          },
    )
    onChanged()
    setBusy(null)
  }

  // The admin's rows carry one more column: when anything last happened.
  const columns = [
    'Name',
    ...variables.map((v) => v.label),
    'Status',
    ...(admin ? ['Last activity'] : []),
    '',
  ]
  const gridColumns = [
    'minmax(180px, 1.6fr)',
    ...variables.map(() => 'minmax(100px, 1fr)'),
    admin ? '180px' : '140px',
    ...(admin ? ['minmax(120px, 1fr)'] : []),
    'minmax(120px, auto)',
  ].join(' ')

  const meta =
    rows.length === 0
      ? 'Nobody added yet'
      : `${rows.length} ${rows.length === 1 ? 'person' : 'people'} · ${unsent.length} not yet sent`

  return (
    <section className="disc-section" aria-labelledby="roster-title">
      <div className="disc-section__head">
        {!admin && (
          <span className="disc-num" aria-hidden="true">
            2
          </span>
        )}
        <div className="disc-section__grow">
          <h3 id="roster-title" className="disc-section__title">
            {admin ? 'Staff' : 'Add your people, then send'}
          </h3>
          <p className="disc-section__meta">{meta}</p>
        </div>
        <div className="disc-section__actions">
          {unsent.length > 0 && (
            <button
              type="button"
              className="btn btn--secondary btn--sm"
              onClick={() => setConfirm({ action: 'send-all' })}
              disabled={busy !== null}
            >
              {busy === 'all' ? 'Sending…' : `Send all unsent (${unsent.length})`}
            </button>
          )}
          {!adding && !space.archived_at && (
            <button type="button" className="btn btn--primary btn--sm" onClick={() => setAdding(true)}>
              Add staff
            </button>
          )}
        </div>
      </div>

      <div className={`flex flex-col${admin ? '' : ' disc-section__body'}`} style={{ gap: 'var(--space-s)' }}>
        {adding && (
          <AddStaffForm
            spaceId={space.id}
            variables={variables}
            prefill={variablePrefill(space)}
            onCreated={(draft) => {
              setAdding(false)
              onFlash({
                type: 'success',
                text: `${draft.first_name} ${draft.last_name} added — not sent yet. Send when you're ready.`,
              })
              onChanged()
            }}
            onCancel={() => setAdding(false)}
          />
        )}

        {rows.length === 0 && !adding && (
          <div
            className="card"
            style={{ textAlign: 'center', paddingTop: 'var(--space-xl)', paddingBottom: 'var(--space-xl)' }}
          >
            <h4 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
              Nobody added yet
            </h4>
            <p className="text-body-md" style={{ color: 'var(--color-text-secondary)' }}>
              {admin
                ? 'Add a staff member to send them an invitation.'
                : 'Add the people you’d like to hear from. Each gets their own invitation.'}
            </p>
          </div>
        )}

        {rows.length > 0 && (
          <div
            className="roster"
            role="table"
            aria-label={admin ? 'Staff' : 'Your people'}
            style={{ '--roster-cols': gridColumns }}
          >
            <div className="roster-row roster-row--head" role="row">
              {columns.map((label, index) => (
                <span key={index} role="columnheader">
                  {label || <span className="sr-only">Actions</span>}
                </span>
              ))}
            </div>
            {rows.map((row) => (
              <div className="roster-row" role="row" key={key(row)}>
                <div className="roster-cell" role="cell">
                  {admin && row.link_id && onOpenResult ? (
                    <button
                      type="button"
                      className="table__link roster-name"
                      // The latest call may sit on an earlier link than the
                      // row's (a reissue), so the call says which.
                      onClick={() => {
                        const last = row.calls[row.calls.length - 1]
                        onOpenResult(last?.link_id ?? row.link_id, last?.attempt ?? null)
                      }}
                      title="Open the write-up"
                    >
                      {name(row)}
                    </button>
                  ) : (
                    <span className="roster-name">{name(row)}</span>
                  )}
                  <span className="roster-email">{row.email}</span>
                </div>
                {variables.map((variable) => (
                  <div className="roster-cell" role="cell" key={variable.key}>
                    <span className="roster-cell__label">{variable.label}</span>
                    <span>{row.variables?.[variable.key] || '—'}</span>
                  </div>
                ))}
                <div className="roster-cell" role="cell">
                  <span className="roster-cell__label">Status</span>
                  <span className="roster-status">
                    <span className="flex items-center flex-wrap" style={{ gap: 'var(--space-xs)' }}>
                      <span className={CHIP_CLASS[row.status] ?? 'chip'}>{row.label}</span>
                      {admin && row.is_new_result && <span className="chip chip--new">New</span>}
                      {admin && row.rating && <RatingMark rating={row.rating} />}
                    </span>
                    {row.calls.length > 0 && (
                      <span className="roster-duration">
                        {row.calls.map((call) => callLength(call.duration_secs)).join(' · ')}
                      </span>
                    )}
                  </span>
                </div>
                {admin && (
                  <div className="roster-cell" role="cell">
                    <span className="roster-cell__label">Last activity</span>
                    <span className={row.last_activity_at ? '' : 'spaces-zero'}>
                      {row.last_activity_at ? formatRelativeActivity(row.last_activity_at) : '—'}
                    </span>
                  </div>
                )}
                <div className="roster-cell roster-actions" role="cell">
                  <RowActions
                    row={row}
                    admin={admin}
                    busy={busy === key(row) || busy === 'all'}
                    onAct={(action) => setConfirm({ action, row })}
                    onCopy={() => copyLink(row)}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {confirm && confirm.action === 'send-all' && (
        <ConfirmDialog label="Send every unsent invitation?" onClose={() => setConfirm(null)}>
          <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
            Send {unsent.length} invitation{unsent.length === 1 ? '' : 's'}?
          </h3>
          <p
            className="text-body-sm"
            style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
          >
            {unsent.map(name).join(', ')} will each get their personal link, from{' '}
            {space.sender_name}, with {admin ? 'the principal' : 'you'} copied.
          </p>
          <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
            <button type="button" className="btn btn--secondary" onClick={() => setConfirm(null)}>
              Not yet
            </button>
            <button type="button" className="btn btn--dark" onClick={sendAll}>
              Send all
            </button>
          </div>
        </ConfirmDialog>
      )}

      {confirm && confirm.action !== 'send-all' && (
        <ActionConfirm
          action={confirm.action}
          row={confirm.row}
          space={space}
          admin={admin}
          onClose={() => setConfirm(null)}
          onConfirm={() => act(confirm.action, confirm.row)}
        />
      )}
    </section>
  )
}

// The verdict on the latest call, display only: it is set on the result page,
// beside the write-up it judges.
function RatingMark({ rating }) {
  const up = rating === 'up'
  return (
    <span
      className={`rating-mark rating-mark--${up ? 'up' : 'down'}`}
      role="img"
      aria-label={up ? 'Rated thumbs up' : 'Rated thumbs down'}
      title={up ? 'Rated thumbs up' : 'Rated thumbs down'}
    >
      <ThumbIcon direction={up ? 'up' : 'down'} size={16} filled />
    </span>
  )
}

// Send is the one filled button; revoke, reissue and copy are quieter — they
// undo or assist rather than progress.
function RowActions({ row, admin, busy, onAct, onCopy }) {
  if (!row.link_id) return null
  const buttons = []
  if (row.status === 'not_sent') {
    buttons.push(
      <button key="send" type="button" className="btn btn--primary btn--sm" onClick={() => onAct('send')} disabled={busy}>
        Send
      </button>,
    )
  }
  if (REVOCABLE.includes(row.status)) {
    buttons.push(
      <button key="revoke" type="button" className="text-btn" onClick={() => onAct('revoke')} disabled={busy}>
        Revoke
      </button>,
    )
  }
  if (REISSUABLE.includes(row.status)) {
    buttons.push(
      <button key="reissue" type="button" className="text-btn" onClick={() => onAct('reissue')} disabled={busy}>
        Reissue
      </button>,
    )
  }
  if (admin && LIVE.includes(row.status)) {
    buttons.push(
      <button key="copy" type="button" className="text-btn" onClick={onCopy} disabled={busy}>
        Copy link
      </button>,
    )
  }
  return buttons.length > 0 ? buttons : null
}

const COPY = {
  send: {
    title: (who) => `Send ${who}'s invitation?`,
    body: (who, space, admin) =>
      `It goes out from ${space.sender_name}, with ${admin ? `${space.principal_name} copied and as the reply address` : 'you copied and as the reply address'}. Their link works until the engagement ends.`,
    button: 'Send',
  },
  revoke: {
    title: (who) => `Revoke ${who}'s link?`,
    body: () => 'It stops working right away. Anything they have already done is kept.',
    button: 'Revoke',
  },
  reissue: {
    title: (who) => `Give ${who} a fresh link?`,
    body: () =>
      'The old link stops working. A new invitation is drafted with the same details, for you to send.',
    button: 'Reissue',
  },
}

function ActionConfirm({ action, row, space, admin, onClose, onConfirm }) {
  const who = `${row.first_name} ${row.last_name}`
  const copy = COPY[action]
  return (
    <ConfirmDialog label={copy.title(who)} onClose={onClose}>
      <h3 className="text-h5" style={{ marginBottom: 'var(--space-xs)' }}>
        {copy.title(who)}
      </h3>
      <p
        className="text-body-sm"
        style={{ color: 'var(--color-text-secondary)', marginBottom: 'var(--space-m)' }}
      >
        {copy.body(who, space, admin)}
      </p>
      <div className="flex justify-end flex-wrap" style={{ gap: 'var(--space-xs)' }}>
        <button type="button" className="btn btn--secondary" onClick={onClose}>
          Cancel
        </button>
        <button type="button" className="btn btn--dark" onClick={onConfirm}>
          {copy.button}
        </button>
      </div>
    </ConfirmDialog>
  )
}
